import { useState } from 'react';
import { DialogActions, DialogContent, Typography } from '@mui/material';
import { RestError } from '@azure/core-http';
import { useQueryClient } from 'react-query';
import { Modal } from '../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../ui/components/pure/text-button/text-button.component';
import { parseErrorMessage } from '../../../core/api/api.context';
import { QueryKeys } from '../../../core/queries/query-keys.constants';
import { useMutationCreateProject } from '../../settings/settings.queries';
import { useMutationCreateCarrierOrganisation } from '../carrier-organisations.queries';
import {
  AddCarrierDetails,
  EMPTY_DETAILS,
  isDetailsStepComplete,
  REGION_PROJECT_TYPE,
  toCreateCarrierOrganisationDto,
  toCreateProjectDto
} from '../logic/add-carrier-form';
import {
  hasPartialProgress,
  isComplete,
  NOT_STARTED,
  RunState,
  StepDefinition,
  viewSteps
} from '../logic/processing-steps';
import { DetailsStep } from '../add-carrier-modal/steps/details-step';
import { ProcessingStep } from '../add-carrier-modal/steps/processing-step';

const PROCESSING: StepDefinition[] = [
  { id: 'project', label: 'Create the region’s project' },
  { id: 'unit', label: 'Set the region up inside its organisation' }
];

export interface AddRegionModalProps {
  /** The carrier project the region belongs to. The region's project becomes a child of it. */
  carrierProjectId: string;
  /** Shown in the copy, so it is clear which carrier this is for. */
  carrierName?: string;
  onSuccess: (regionProjectId: string, regionName: string) => void;
  onCancel: () => void;
}

/** One step: a region needs no network picker, inheriting the servicing its organisation holds. */
export function AddRegionModal({ carrierProjectId, carrierName, onSuccess, onCancel }: AddRegionModalProps) {
  const [details, setDetails] = useState<AddCarrierDetails>(EMPTY_DETAILS);
  const [showValidation, setShowValidation] = useState(false);
  const [shortcodeError, setShortcodeError] = useState<string | undefined>();
  // As in the carrier wizard: a retry finishes this project rather than making a second.
  const [regionProjectId, setRegionProjectId] = useState<string | undefined>();
  const [run, setRun] = useState<RunState>(NOT_STARTED);
  const [isProcessing, setIsProcessing] = useState(false);

  const createProject = useMutationCreateProject();
  const createOperatingUnit = useMutationCreateCarrierOrganisation();
  const queryClient = useQueryClient();

  const isSubmitting = createProject.isLoading || createOperatingUnit.isLoading;

  const onCreate = async () => {
    setShowValidation(true);
    if (!isDetailsStepComplete(details)) {
      return;
    }

    setShortcodeError(undefined);
    setIsProcessing(true);

    let projectId = regionProjectId;
    let completed = run.completed;

    for (const definition of PROCESSING) {
      if (completed.includes(definition.id)) {
        continue;
      }
      setRun({ completed, running: definition.id });

      try {
        if (definition.id === 'project') {
          const project = await createProject.mutateAsync({
            dto: toCreateProjectDto(details, carrierProjectId, REGION_PROJECT_TYPE)
          });
          projectId = project.id;
          setRegionProjectId(project.id);
        } else {
          // No servicing: a region inherits what its organisation was granted.
          await createOperatingUnit.mutateAsync({ dto: toCreateCarrierOrganisationDto(projectId as string) });
        }
        completed = [...completed, definition.id];
      } catch (e) {
        const restError = e as RestError;
        if (definition.id === 'project' && restError.statusCode === 409) {
          setRun(NOT_STARTED);
          setIsProcessing(false);
          setShortcodeError('That shortcode is already in use');
          return;
        }
        setRun({ completed, failed: { id: definition.id, error: parseErrorMessage(restError.message) || 'Failed.' } });
        return;
      }
    }

    setRun({ completed });
    queryClient.invalidateQueries(QueryKeys.SETTINGS);
    queryClient.invalidateQueries(QueryKeys.PROJECTS);
    queryClient.invalidateQueries(QueryKeys.CARRIERS);
    onSuccess(projectId as string, details.name.trim());
  };

  return (
    <Modal title="Add Region" open divider maxWidth="sm" onClose={isSubmitting ? undefined : onCancel}>
      <DialogContent style={{ padding: '16px 24px' }}>
        {isProcessing ? (
          <ProcessingStep steps={viewSteps(PROCESSING, run)} isRepairing={hasPartialProgress(run)} />
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              A region is a part of {carrierName ?? 'this carrier'} with its own admins and its own couriers. It works
              in the same networks as the carrier, and kiosks still show the carrier’s name.
            </Typography>

            <DetailsStep
              details={details}
              onChange={(next) => {
                setShortcodeError(undefined);
                setDetails(next);
              }}
              readOnly={!!regionProjectId}
              shortcodeError={shortcodeError}
              showValidation={showValidation}
            />
          </>
        )}
      </DialogContent>

      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton color="primary" variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </TextButton>
        <TextButton
          color="primary"
          variant="contained"
          onClick={onCreate}
          isLoading={isSubmitting}
          disabled={isSubmitting || (isProcessing && isComplete(PROCESSING, run))}
        >
          {isProcessing ? 'Try again' : 'Create'}
        </TextButton>
      </DialogActions>
    </Modal>
  );
}
